import { useSideMenu } from '@/components/NewHeader/SideMenu/useSideMenu';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import throttle from 'lodash.throttle';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

type State = {
  isScrolled: boolean;
  isHover: boolean;
  selectedNavItem: string;
  setIsScrolled: (isScrolled: boolean) => void;
  setIsHover: (isHover: boolean) => void;
  setSelectedNavItem: (selectedNavItem: string) => void;
  close: () => void;
};

const useStore = create<State>()((set) => ({
  isScrolled: false,
  isHover: false,
  selectedNavItem: '',
  setIsScrolled: (isScrolled: boolean) => set(() => ({ isScrolled })),
  setIsHover: (isHover: boolean) => set(() => ({ isHover })),
  setSelectedNavItem: (selectedNavItem: string) =>
    set(() => ({ selectedNavItem })),
  close: () => set(() => ({ selectedNavItem: '' })),
}));

export function useHeaderScroll() {
  const {
    isScrolled,
    setIsScrolled,
    isHover,
    setIsHover,
    selectedNavItem,
    setSelectedNavItem,
    close,
  } = useStore();
  const pathname = usePathname();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { isOpen } = useSideMenu();
  const isHome = ['/', '/de', '/it', '/fr'].includes(pathname);

  const isActive = useMemo(() => {
    if (!isHome) {
      return true;
    } else if (isDesktop) {
      return isHover || isScrolled || selectedNavItem !== '';
    } else {
      return isScrolled || isOpen;
    }
  }, [isDesktop, isHome, isHover, isOpen, isScrolled, selectedNavItem]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    const throttledScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, [setIsScrolled]);

  return {
    isActive,
    setIsHover,
    selectedNavItem,
    setSelectedNavItem,
    isHome,
    close,
    isDesktop,
  };
}
